/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPIHousien } from '../core/OpenAPI';
import { request as __request } from '../core/request';

/** A quota as the team screen draws it: what was used, out of what. */
export interface TeamQuota {
  used: number;
  total: number | null;
}

/** One plan's seats. `free` is already clamped to what the agency has left. */
export interface TeamSeatBlock {
  plan_id: string;
  bought: number;
  taken: number;
  free: number;
}

/**
 * The agency's seats.
 *
 * `bought`, `free` and `by_plan` are null when the subscription never recorded
 * its lines — that is "not known", never a ceiling of zero.
 */
export interface TeamSeats {
  bought: number | null;
  taken: number;
  free: number | null;
  /** Holders whose chair names no plan — the buyer, until they pick. */
  unassigned?: number;
  by_plan: TeamSeatBlock[] | null;
}

/** One seated person, with the allowance the seat they sit in carries. */
export interface TeamPerson {
  /** When this seat empties, or null when it is staying. */
  leaving_at?: string | null;
  seat_id: string | null;
  email: string;
  role: string | null;
  first_name: string;
  last_name: string;
  member_since: string | null;
  plan_id: string | null;
  interaction: TeamQuota;
  partnership_search: TeamQuota;
}

/** An invitation still holding a seat. `plan_id` names the card it belongs to. */
export interface TeamPendingInvite {
  /** The handle the revoke route takes. */
  id: string | null;
  email: string;
  role: string | null;
  plan_id: string | null;
  created_at: string | null;
  expires_at: string | null;
}

/** The agency's one shared drive. Both halves are null when unknown. */
export interface TeamStorage {
  used_bytes: number | null;
  total_gb: number | null;
}

/** Everything the team and invite screens draw, in one answer. */
/**
 * Free auxiliary places the paid seats bring, counted apart from them.
 *
 * Never added to `seats`: a team can be out of paid seats and still have
 * room for a receptionist, and the other way round. Derived from the
 * subscription on every read, so it is never stale and never stored.
 */
export interface TeamSupportSeats {
  total: number;
  used: number;
  free: number;
}

export interface TeamOverview {
  support_seats?: TeamSupportSeats | null;
  storage: TeamStorage;
  seats: TeamSeats;
  people: TeamPerson[];
  vacant: Array<{
    seat_id: string | null;
    plan_id: string | null;
    leaving_at?: string | null;
  }>;
  pending: TeamPendingInvite[];
  /** The reduction waiting for the renewal, or null when there is none. */
  scheduled_items_change?: {
    items: Array<{ plan_id: string; quantity: number }>;
    effective_at: string;
  } | null;
}

/** One typed line, as the batch route reads it. */
export interface InviteEntry {
  /**
   * Which kind of place the invitation holds open. Absent means a paid
   * seat, the same rule the roster follows. The server decides the role
   * for an auxiliary place regardless of what is sent here.
   */
  seat_kind?: string;
  email: string;
  role: string;
  plan_id?: string | null;
}

/**
 * Why one address was refused, or null when the invitation went out.
 *
 * `plan_full` is the per-plan ceiling and `no_seats` the agency-wide one: an
 * agency can be out of Rubi seats while still having Esmeralda ones.
 */
export type InviteRefusal =
  | 'invalid'
  | 'duplicate'
  | 'already_member'
  | 'already_invited'
  | 'no_seats'
  | 'plan_full'
  | 'send_failed';

/** What happened to one typed line. */
export interface InviteVerdict extends InviteEntry {
  ok: boolean;
  reason: InviteRefusal | null;
}

/** Every line's verdict, and how many invitations actually went out. */
export interface InviteBatchResult {
  results: InviteVerdict[];
  sent: number;
}

/**
 * Reads the whole team in one request via `GET /agencies/{agencyId}/team`.
 *
 * The usage route answers about whoever is asking; this answers about the
 * agency, because the screens draw a row per person and one request per seat
 * would be a page that fills in pieces.
 *
 * @param agencyId - The agency identifier
 * @returns A cancelable promise resolving to the team overview
 */
export function getAgencyTeam(
  agencyId: string,
): CancelablePromise<TeamOverview> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agencies/{agencyId}/team',
    path: { agencyId },
  });
}

/**
 * Invites several people at once via `POST /agencies/{agencyId}/invitations/batch`.
 *
 * Answers 200 with one verdict per address rather than failing the request: a
 * typo on the fourth line must not make the operator retype the three that
 * were fine.
 *
 * @param agencyId - The agency identifier
 * @param entries - The typed lines, each with an email, a role and a plan
 * @param message - Optional note carried into every invitation e-mail
 * @returns A cancelable promise resolving to a verdict per line
 */
export function inviteAgencyMembers(
  agencyId: string,
  entries: InviteEntry[],
  message = '',
): CancelablePromise<InviteBatchResult> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agencies/{agencyId}/invitations/batch',
    path: { agencyId },
    body: { invitations: entries, message },
    mediaType: 'application/json',
  });
}

/** Why a move was refused, or null when it happened. */
export type SeatMoveRefusal =
  | 'not_a_member'
  | 'unknown_plan'
  | 'already_there'
  | 'plan_full';

/** What happened to one move. */
export interface SeatMoveResult {
  ok: boolean;
  reason: SeatMoveRefusal | null;
  plan_id: string;
}

/**
 * Seats a member under a different plan via `PUT /agencies/{agencyId}/member/seat`.
 *
 * The subscription does not move: what was bought stays bought, and only who
 * sits where changes. The seat's plan is what sizes that person's allowance,
 * so this is a billing change to them.
 *
 * @param agencyId - The agency identifier
 * @param userEmail - The member being moved
 * @param planId - The plan whose seat they are moving into
 * @returns A cancelable promise resolving to the verdict
 */
export function moveMemberSeat(
  agencyId: string,
  userEmail: string,
  planId: string,
): CancelablePromise<SeatMoveResult> {
  return __request(OpenAPIHousien, {
    method: 'PUT',
    url: '/agencies/{agencyId}/member/seat',
    path: { agencyId },
    body: { user_email: userEmail, plan_id: planId },
    mediaType: 'application/json',
  });
}

/** One seat chosen to empty when a scheduled reduction lands. */
export interface LeavingSeat {
  plan_id: string;
  kind: 'person' | 'vacant' | 'invitation';
  seat_id?: string | null;
  slot?: number | null;
  invitation_id?: string | null;
  email?: string | null;
  cancelled_at?: string | null;
}

/** The subscription's lines, as the seat controls send them. */
export interface SubscriptionItemInput {
  plan_id: string;
  seats: number;
}

/** What came back from changing the lines. */
export interface ChangeItemsResult {
  subscription_id: string;
  items: Array<{ plan_id: string; quantity: number }> | null;
  subscription_status: string | null;
  charge_id: string | null;
  effective_at: string | null;
  next_renewal_date: string | null;
  scheduled_items_change: {
    items: Array<{ plan_id: string; quantity: number }>;
    effective_at: string;
  } | null;
}

/**
 * What Payien would charge for a given list of seats, before it is bought.
 *
 * The screens used to work this out themselves, from a billing cycle this
 * API did not send them -- so an annual subscription was prorated over
 * thirty days. Price belongs to the service that collects it.
 */
export interface ChangeItemsPreview {
  /** What asking for this list would do. */
  outcome: 'grow' | 'shrink' | 'unchanged';
  /** Collected now; zero unless the list costs more than the live one. */
  charged_today_cents: number;
  /**
   * Only in the free period (`charged_today_cents` is then 0): the total of
   * the new list, charged when the free period ends. Absent once active.
   */
  next_charge_amount_cents?: number;
  new_total_cents: number;
  current_total_cents: number;
  /**
   * True when this list cancels a user reduction scheduled for the next
   * renewal. Absent or false otherwise.
   */
  scheduled_seat_change_cleared?: boolean;
  /** When a reduction lands; empty when the change applies at once. */
  effective_at: string;
  billing_cycle_days: number;
  days_remaining: number;
  items: Array<{
    plan_id: string;
    name: string;
    quantity: number;
    unit_price_cents: number;
    total_cents: number;
  }>;
}

/**
 * Asks what a list of seats would cost, via
 * `POST /agencies/{agencyId}/subscription/change-items/preview`.
 *
 * Nothing is charged and nothing is written: this is the same question
 * `changeSubscriptionItems` answers by doing, asked without doing it.
 *
 * @param agencyId - The agency identifier
 * @param items - The lines being considered
 * @returns A cancelable promise resolving to what it would cost
 */
export function previewSubscriptionItems(
  agencyId: string,
  items: SubscriptionItemInput[],
): CancelablePromise<ChangeItemsPreview> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agencies/{agencyId}/subscription/change-items/preview',
    path: { agencyId },
    body: { items },
    mediaType: 'application/json',
  });
}

/**
 * Changes how many seats of each plan the agency pays for, via
 * `POST /agencies/{agencyId}/subscription/change-items`.
 *
 * Growing charges the pro-rata difference now; shrinking is scheduled for the
 * next renewal with no refund. Asking for the list the subscription already
 * has is how a scheduled reduction is cancelled.
 *
 * @param agencyId - The agency identifier
 * @param items - The lines the subscription should hold
 * @returns A cancelable promise resolving to the subscription as it stands
 */
export function changeSubscriptionItems(
  agencyId: string,
  items: SubscriptionItemInput[],
): CancelablePromise<ChangeItemsResult> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agencies/{agencyId}/subscription/change-items',
    path: { agencyId },
    body: { items },
    mediaType: 'application/json',
  });
}

/**
 * Records which seats empty when the scheduled reduction lands, via
 * `PUT /agencies/{agencyId}/subscription/leaving`.
 *
 * The whole list is sent rather than one click at a time, so the same
 * request twice is the same answer. A chosen invitation is revoked on the
 * spot and comes back carrying `cancelled_at`.
 *
 * @param agencyId - The agency identifier
 * @param leaving - The seats chosen to empty, in choice order
 * @returns A cancelable promise resolving to the stored choice
 */
export function setSubscriptionLeaving(
  agencyId: string,
  leaving: LeavingSeat[],
): CancelablePromise<{ leaving: LeavingSeat[]; complete: boolean }> {
  return __request(OpenAPIHousien, {
    method: 'PUT',
    url: '/agencies/{agencyId}/subscription/leaving',
    path: { agencyId },
    body: { leaving },
    mediaType: 'application/json',
  });
}

/** Payien's verdict on a coupon code, relayed by the host. */
export interface CouponVerdictResponse {
  valid: boolean;
  code?: string;
  discount_type?: string | null;
  discount_value?: number | null;
  discount_amount_cents?: number;
  final_amount_cents?: number;
  message?: string;
}

/**
 * Checks a coupon before the checkout session is rebuilt, via
 * `GET /agencies/{agencyId}/subscription/coupon`.
 *
 * A refused code answers 200 with `valid: false` and the reason — the buyer
 * has to read it, and a failure would say the checkout is broken when it is
 * the code that is.
 *
 * @param agencyId - The agency identifier
 * @param code - The code as typed
 * @param amountCents - What the purchase costs before any discount
 * @returns A cancelable promise resolving to the verdict
 */
export function validateCoupon(
  agencyId: string,
  code: string,
  amountCents: number,
  billingFrequency?: string,
): CancelablePromise<CouponVerdictResponse> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agencies/{agencyId}/subscription/coupon',
    path: { agencyId },
    query: {
      code,
      amount_cents: amountCents,
      ...(billingFrequency ? { billing_frequency: billingFrequency } : {}),
    },
  });
}

/** What a resend or a link hand-off leaves the caller. */
export interface InvitationLinkResponse {
  /** Only on the link call: a resend has nothing to show. */
  url?: string;
  /** When the freshly minted token dies, so a pasted link can be dated. */
  expires_at?: string;
}

/**
 * Sends a pending invitation again, via
 * `POST /agencies/{agencyId}/invitations/{invitationId}/resend`.
 *
 * The token is minted fresh, so the record's three hours start again -- the
 * old one would expire while the mail just sent was still in an inbox.
 *
 * @param agencyId - The agency identifier
 * @param invitationId - The pending invitation
 * @returns A cancelable promise resolving when the mail is away
 */
export function resendInvitation(
  agencyId: string,
  invitationId: string,
): CancelablePromise<InvitationLinkResponse> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agencies/{agencyId}/invitations/{invitationId}/resend',
    path: { agencyId, invitationId },
  });
}

/**
 * The link for a pending invitation, via
 * `GET /agencies/{agencyId}/invitations/{invitationId}/link`.
 *
 * For handing over by other means -- the operator pastes it into whatever
 * the invitee actually reads. Reading, not sending: no mail goes out.
 *
 * @param agencyId - The agency identifier
 * @param invitationId - The pending invitation
 * @returns A cancelable promise resolving to the link and its expiry
 */
export function invitationLink(
  agencyId: string,
  invitationId: string,
): CancelablePromise<InvitationLinkResponse> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agencies/{agencyId}/invitations/{invitationId}/link',
    path: { agencyId, invitationId },
  });
}
